<template>
  <v-dialog v-model="dialogOpen" max-width="640" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon size="20" color="primary">mdi-account-plus-outline</v-icon>
        <span class="text-subtitle-1">
          {{ $t("ncrm.integrations.mailjet.addContact.title") }}
        </span>
      </v-card-title>
      <v-card-subtitle v-if="contextSubtitle" class="pb-2">
        {{ contextSubtitle }}
      </v-card-subtitle>
      <v-divider />

      <v-card-text class="pa-4" style="max-height: 540px;">
        <v-skeleton-loader v-if="loading" type="paragraph@4" :loading="true" />

        <template v-else>
          <v-select
            v-if="accountItems.length > 1"
            :model-value="selectedAccountId"
            :items="accountItems"
            :label="$t('ncrm.integrations.mailjet.addContact.fields.account')"
            class="mb-2"
            @update:model-value="onAccountSelected"
          />
          <v-text-field
            v-model="form.email"
            :label="$t('ncrm.integrations.mailjet.addContact.fields.email')"
            type="email"
            :rules="emailRules"
            required
            class="mb-2"
          />
          <v-text-field
            v-model="form.name"
            :label="$t('ncrm.integrations.mailjet.addContact.fields.name')"
            class="mb-2"
          />
          <v-checkbox
            v-model="form.isExcludedFromCampaigns"
            :label="$t('ncrm.integrations.mailjet.addContact.fields.isExcluded')"
            density="compact"
            hide-details
            class="mb-3"
          />

          <!-- Custom properties section -->
          <div
            v-if="customProperties.length"
            class="text-caption text-medium-emphasis mb-2"
          >
            {{ $t("ncrm.integrations.mailjet.addContact.customSectionTitle") }}
          </div>
          <div
            v-for="prop in customProperties"
            :key="prop.mailjet_property"
            class="mb-2"
          >
            <!-- Booleans render as v-checkbox -->
            <v-checkbox
              v-if="prop.mailjet_property_type === 'bool'"
              v-model="form.properties[prop.mailjet_property]"
              :label="propertyLabel(prop)"
              :hint="propertyHint(prop)"
              persistent-hint
              density="compact"
              hide-details="auto"
            />
            <!-- Datetime renders as a plain text input (operator may type ISO) -->
            <v-text-field
              v-else
              v-model="form.properties[prop.mailjet_property]"
              :label="propertyLabel(prop)"
              :hint="propertyHint(prop)"
              persistent-hint
              :type="prop.mailjet_property_type === 'int' ? 'number' : 'text'"
            />
          </div>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="text" :disabled="submitting" @click="cancel">
          {{ $t("ncrm.integrations.mailjet.addContact.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        >
          <v-icon start size="16">mdi-content-save-outline</v-icon>
          {{ $t("ncrm.integrations.mailjet.addContact.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmMailjetAddContactDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    accountId: { type: [Number, String], default: null },
    accounts: { type: Array, default: () => [] },
    entityType: { type: String, required: true },
    entityId: { type: [Number, String], required: true },
    // Optional: when set, overrides the prefilled email (used when the
    // operator picks a specific entity email from the unlinked list).
    presetEmail: { type: String, default: "" },
  },
  emits: ["update:modelValue", "saved", "notify"],
  data() {
    return {
      loading: false,
      submitting: false,
      selectedAccountId: null,
      prefill: null,
      form: {
        email: "",
        name: "",
        isExcludedFromCampaigns: false,
        properties: {},
      },
    };
  },
  computed: {
    dialogOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    contextSubtitle() {
      if (!this.prefill) return "";
      const parts = [];
      if (this.prefill.person?.id) {
        const fullName = [this.prefill.person.first_name, this.prefill.person.last_name]
          .filter(Boolean)
          .join(" ").trim();
        parts.push(fullName || `Person #${this.prefill.person.id}`);
      }
      if (this.prefill.company?.id) {
        parts.push(this.prefill.company.name || `Company #${this.prefill.company.id}`);
      }
      return parts.join(" • ");
    },
    customProperties() {
      if (!this.prefill?.properties) return [];
      return this.prefill.properties.filter((p) => !p.is_builtin);
    },
    emailRules() {
      return [
        (v) => Boolean(v) || this.$t("ncrm.integrations.mailjet.addContact.errors.emailRequired"),
        (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "")) ||
          this.$t("ncrm.integrations.mailjet.addContact.errors.emailInvalid"),
      ];
    },
    canSubmit() {
      const email = String(this.form.email || "").trim();
      return Boolean(this.selectedAccountId) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !this.submitting;
    },
    accountItems() {
      return (Array.isArray(this.accounts) ? this.accounts : [])
        .filter((account) => account?.id)
        .map((account) => ({
          title: account.name || `#${account.id}`,
          value: account.id,
        }));
    },
  },
  watch: {
    modelValue: {
      handler(open) {
        if (open) this.openDialog();
      },
      immediate: true,
    },
  },
  methods: {
    propertyLabel(prop) {
      return prop.mailjet_property;
    },
    propertyHint(prop) {
      // Show the admin-configured CRM source — keeps the mapping transparent
      // to the operator without letting them change it. If unmapped, show
      // a placeholder hint.
      if (prop.crm_source_field) {
        return this.$t("ncrm.integrations.mailjet.addContact.fields.mappedFromHint", {
          source: prop.crm_source_field,
        });
      }
      return this.$t("ncrm.integrations.mailjet.addContact.fields.unmappedHint");
    },
    cancel() {
      this.dialogOpen = false;
    },
    onAccountSelected(value) {
      if (!value || value === this.selectedAccountId) return;
      this.selectedAccountId = value;
      this.loadPrefill();
    },
    async openDialog() {
      const fallback = this.accountItems[0]?.value || this.accountId || null;
      this.selectedAccountId = this.accountId || fallback;
      await this.loadPrefill();
    },
    async loadPrefill() {
      if (!this.selectedAccountId) return;
      this.loading = true;
      this.prefill = null;
      this.form = {
        email: "",
        name: "",
        isExcludedFromCampaigns: false,
        properties: {},
      };
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.selectedAccountId}/contact-form-prefill`,
          { params: { entityType: this.entityType, entityId: this.entityId } }
        );
        this.prefill = data;
        // Operator clicked a specific email row → honour that choice over
        // whichever email the mapping happened to resolve.
        const preset = String(this.presetEmail || "").trim();
        this.form.email = preset || data?.email || "";
        this.form.name = data?.name || "";
        this.form.isExcludedFromCampaigns = Boolean(data?.is_excluded_from_campaigns);
        // Seed custom property values (skip built-ins; they have their own inputs).
        const propBag = {};
        for (const prop of data?.properties || []) {
          if (prop.is_builtin) continue;
          propBag[prop.mailjet_property] = prop.value ?? "";
        }
        this.form.properties = propBag;
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message:
            error?.response?.data?.error ||
            error?.message ||
            this.$t("ncrm.integrations.mailjet.addContact.errors.prefill"),
        });
        this.dialogOpen = false;
      } finally {
        this.loading = false;
      }
    },
    buildSubmitPayload() {
      const properties = [];
      for (const [name, value] of Object.entries(this.form.properties || {})) {
        if (value === null || value === undefined || value === "") continue;
        properties.push({ name, value });
      }
      return {
        entityType: this.entityType,
        entityId: this.entityId,
        email: String(this.form.email || "").trim().toLowerCase(),
        name: this.form.name || null,
        isExcludedFromCampaigns: Boolean(this.form.isExcludedFromCampaigns),
        properties,
      };
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.selectedAccountId}/contacts`,
          this.buildSubmitPayload()
        );
        const messageKey =
          data?.mode === "linked"
            ? "ncrm.integrations.mailjet.addContact.alerts.linked"
            : "ncrm.integrations.mailjet.addContact.alerts.created";
        this.$emit("notify", { color: "success", message: this.$t(messageKey) });
        this.$emit("saved", data);
        this.dialogOpen = false;
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message:
            error?.response?.data?.error ||
            error?.message ||
            this.$t("ncrm.integrations.mailjet.addContact.errors.submit"),
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
